import { useToast } from 'hooks/useToast';
import { apiClient } from 'api';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface CoreResponsibilitiesPayload {
  assessment_id: string;
  [key: string]: string | boolean;
}

export interface DeleteCoreResponsibilitiesPayload {
  assessment_id: string;
}

/**
 * Hook for submitting core responsibilities
 */
export const useCoreResponsibilitiesEdit = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const patchMutation = useMutation({
    mutationFn: async (payload: CoreResponsibilitiesPayload) => {
      const response = await apiClient.patch('/assessment/core-responsibility/', payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['coreResponsibilities', assessmentId] });
      openToast({
        show: true,
        heading: "Edit Successful",
        text: "Core responsibilities edited successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to submit core responsibilities';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    submitCoreResponsibilities: patchMutation.mutate,
    isSubmitting: patchMutation.isPending,
  };
};

/**
 * Hook for deleting core responsibilities
 */
export const useCoreResponsibilitiesDelete = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const deleteMutation = useMutation({
    mutationFn: async (payload: DeleteCoreResponsibilitiesPayload) => {
      const response = await apiClient.delete('/assessment/core-responsibility/', {
        data: payload,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['coreResponsibilities', assessmentId] });
      openToast({
        show: true,
        heading: "Delete Successful",
        text: "Core responsibilities deleted successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to delete core responsibilities';
      openToast({
        show: true,
        heading: "Delete Failed",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    deleteCoreResponsibilities: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};